import React, { useState, useEffect } from 'react';
import PageTitle from 'src/components/page-title';
import Divider from 'src/components/divider';
import Table from 'src/pages/responsible/table';
import Button, { Type as ButtonType } from 'src/components/button';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import FormModal from 'src/pages/responsible/form-modal';
import CenterLoading from 'src/components/center-loading';
import usePermission from 'src/hooks/use-permission';
import { get } from 'lodash';
import './style.scss';
import { Redirect } from 'react-router-dom';
import Filters from './responsible-filter';

/**
 * Renders the Responsible page.
 * @param {Object} props - The component props.
 * @param {Array} props.profiles - The list of profiles.
 * @param {Array} props.positionMaps - The list of position maps.
 * @param {Array} props.responsibles - The list of responsibles.
 * @param {Function} props.onPatchResponsible - The function to patch a responsible.
 * @param {Function} props.onCreateResponsible - The function to create a responsible.
 * @param {Function} props.onUpdateResponsible - The function to update a responsible.
 * @param {Function} props.refreshResponsibles - The function to refresh the list of responsibles.
 * @returns {JSX.Element} - The rendered component.
 */

function Responsible({ 
    profiles,
    positionMaps,
    responsibles,
    onPatchResponsible,
    onCreateResponsible,
    onUpdateResponsible,
    refreshResponsibles,
    profileOptions,
}) {
    const permissions = usePermission();
    const canUpdate = get(
        permissions,
        'responsiblePermissions.canUpdate'
    );
    const canCreate = get(
        permissions,
        'responsiblePermissions.canCreate'
    );
    const { t } = useTranslation();
    const [createModal, setCreatemodal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginAndNameFilter, setLoginAndNameFilter] = useState('');
    const [selectedProfile, setSelectedProfile] = useState('');
    const [filteredResponsibles, setFilteredResponsibles] = useState([]);

    const patchResponsible = async (id, status) => {
        setLoading(true);
        try {
            const disabledResponsibleResponse =
                await onPatchResponsible(id, status);

            if (disabledResponsibleResponse.status === 200) {
                refreshResponsibles();
                if (status === "Y") {
                    notification.success({message: t("success.responsibles.activate")});
                } else {
                    notification.success({message: t("success.responsibles.deactivate")});
                }
            }
        } catch ({ message }) {
            setLoading(false);
            console.log({message: t("errors.responsibles.toggleUser")});
        }
        setLoading(false);
    }

    const createResponsible = async (responsibleData) => {
        setLoading(true);
        try {
            await onCreateResponsible(responsibleData);
            setCreatemodal(false);
            refreshResponsibles();
            notification.success({message: t("success.responsibles.create")});
        } catch ({ message }) {
            setLoading(false);
            console.log({message: t("errors.responsibles.create")});
    }
        setLoading(false);
    }

    const updateResponsible = async (responsibleData) => {
        setLoading(true);
        try {
            const newResponsible = await onUpdateResponsible(responsibleData);
            if (newResponsible.status === 200) {
                refreshResponsibles();
                notification.success({
                    message: t('successEditing'),
                });
                setCreatemodal(false);
            }
        } catch ({ message }) {
            setLoading(false);
            console.log({message: t("errors.responsibles.edit")});
    }
        setLoading(false);
    }

    useEffect(() => {
        const filteredResponsibles = responsibles.filter((responsible) => {
            const name = responsible.name.toLowerCase();
            const login = responsible.login.toLowerCase();
            const loginAndName = `${name} ${login}`.toLowerCase();
        
            const nameMatch = name.includes(loginAndNameFilter.toLowerCase());
            const loginMatch = login.includes(loginAndNameFilter.toLowerCase());
            const loginAndNameMatch = loginAndName.includes(loginAndNameFilter.toLowerCase());
        
            const profileMatch = selectedProfile === '' || responsible.profile === selectedProfile;
        
            return (nameMatch || loginMatch || loginAndNameMatch) && profileMatch;
        });
        
        setFilteredResponsibles(filteredResponsibles);
    }, [responsibles, loginAndNameFilter, selectedProfile]);

    return (
        <div className='responsible'>
            {canUpdate === false && <Redirect to='/people' /> && notification.error({message: t('errors.access')})}
            <PageTitle title={t('responsibleTitle')} />
            <Divider />
            <div className='responsible-table'>
                <div className='responsible-table__add'>
                    <div className='responsible-table__add_button'>
                        {canCreate && (
                            <Button
                            className='create-deallocation-btn'
                            type={ButtonType.Primary}
                            uppercase
                            onClick={() => setCreatemodal(true)}
                            >
                                {t('addResponsible')}
                            </Button>
                        )}
                    </div>
                </div>
                <Filters
                    onSearch={(searchLogin, selectedProfile) => {
                        setLoginAndNameFilter(searchLogin);
                        setSelectedProfile(selectedProfile);
                    }}
                    profileOptions={profileOptions}
                />
                <div className='responsible-table__container'>
                    {canCreate && !loading && (
                        <Table
                            data={filteredResponsibles}
                            patchResponsible={(id, status) => patchResponsible(id, status)}
                            edit={(responsible) => setCreatemodal(responsible)}
                        />
                    )}
                    {loading && <CenterLoading />}
                    {canCreate && createModal && (
                        <FormModal
                            onClose={() => setCreatemodal(false)}
                            createResponsible={(data) => createResponsible(data)}
                            updateResponsible={(data) => updateResponsible(data)}
                            loading={false}
                            responsible={createModal}
                            profiles={profiles}
                            positionMaps={positionMaps}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export default Responsible;